import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React from 'react';
import { Button as Button$1 } from '@mui/base/Button';
import classNames from 'classnames';
import { LoaderIcon } from '@cognite/cogs-icons';
import { warn, getThemeClassNames, IconWrapper } from '@cognite/cogs-utils';
import { CounterChip } from '../counter-chip/counter-chip.js';

/**
 * Button
 * =============================================================================
 * The button can be used to show a user that they can click a certain area.
 * This could be any action, For example, a Call to Action, but also a
 * close-button in a modal.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-button--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Button onClick={clickHandler}>
 *   My button
 * </Button>
 *
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param alignVertically  - Align based on the content instead of the outside.
 * @param disabled         - Disable the button.
 * @param htmlType         - Set the native HTML type.
 * @param icon             - Add an icon to the button.
 * @param iconPlacement    - Position the icon left or right on the button.
 * @param inverted         - Style the button for use on a dark surface.
 * @param loading          - Show a loader and disable the button.
 * @param size             - The size of the button.
 * @param skeleton         - The skeleton version of the button.
 * @param toggled          - Set the "Toggled" state of the button.
 * @param type             - The type of button.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-button--base)
 */
var Button = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var children = _a.children,
    _a$className = _a.className,
    className = _a$className === undefined ? '' : _a$className,
    disabled = _a.disabled,
    _a$htmlType = _a.htmlType,
    htmlType = _a$htmlType === undefined ? 'button' : _a$htmlType,
    icon = _a.icon,
    _a$iconPlacement = _a.iconPlacement,
    iconPlacement = _a$iconPlacement === undefined ? 'left' : _a$iconPlacement,
    alignVertically = _a.alignVertically,
    inverted = _a.inverted,
    loading = _a.loading,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    skeleton = _a.skeleton,
    toggled = _a.toggled,
    _a$type = _a.type,
    type = _a$type === undefined ? 'secondary' : _a$type,
    themeProps = _a.themeProps,
    counterChip = _a.counterChip,
    otherProps = __rest(_a, ["children", "className", "disabled", "htmlType", "icon", "iconPlacement", "alignVertically", "inverted", "loading", "size", "skeleton", "toggled", "type", "themeProps", "counterChip"]);
  if (icon && !children && !otherProps['aria-label']) {
    warn("Cogs accessibility warning: You should not have an icon-only button without an aria-label: \"" + icon + "\". Please add the aria-label to the button.");
  }
  return jsxs(Button$1, Object.assign({
    focusableWhenDisabled: true,
    type: htmlType,
    ref: ref,
    disabled: disabled || loading,
    "aria-busy": loading,
    "aria-pressed": toggled,
    className: skeleton ? classNames.apply(undefined, ['cogs-v10', 'cogs-button', 'cogs-button--skeleton'].concat(getThemeClassNames(inverted, themeProps), [className])) : classNames.apply(undefined, ['cogs-v10', 'cogs-button', "cogs-button--type-" + type, "cogs-button--size-" + size].concat(getThemeClassNames(inverted, themeProps), [loading && 'cogs-button--loading', disabled && 'cogs-button--disabled', icon && !children && 'cogs-button--icon-only', icon && children && "cogs-button--icon-" + iconPlacement, toggled && 'cogs-button--toggled', alignVertically && "cogs-button--align-vertically-" + alignVertically, className]))
  }, otherProps, {
    children: [icon && iconPlacement === 'left' && jsx(IconWrapper, {
      size: 16,
      children: icon
    }), children, icon && iconPlacement === 'right' && jsx(IconWrapper, {
      size: 16,
      children: icon
    }), loading && jsx(IconWrapper, {
      size: 16,
      className: "cogs-v10 cogs-loading-icon-container",
      children: jsx(LoaderIcon, {})
    }), toggled && counterChip && jsx(CounterChip, Object.assign({
      className: "cogs-v10 cogs-button__counterchip"
    }, counterChip))]
  }));
});
Button.displayName = 'CogsButton';

export { Button };
